import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

/**
 * 149-db (PROMPT 16J-ASSET-LOCATION-R2-R1): shared grouped location picker.
 * Sumber gabungan (hybrid): manual asset_locations + parent project Aktif/Closing.
 * Item id yang diawali `proj:<pid>` di-materialize ke asset_location saat pertama
 * dipilih (dilakukan di parent form via prepareLocationId helper).
 */
export function useLocationOptions() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/assets/locations/hybrid");
      setGroups(data.groups || []);
    } catch { setGroups([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return { groups, loading, reload: load };
}

/**
 * If the picked id is `proj:<pid>`, POST to backend to materialize into a
 * real asset_location record and return its id. Otherwise return id as-is.
 * Called by parent form just before submitting the asset/movement.
 */
export async function resolveLocationId(id) {
  if (!id) return id;
  if (id.startsWith("proj:")) {
    const pid = id.slice(5);
    const { data } = await api.post("/assets/locations/materialize", { project_id: pid });
    return data.id;
  }
  return id;
}

/**
 * 16-ASSET-PIC-LOCATION-R1: resolve manual location NAME → id (create if
 * missing). Idempotent case-insensitive+whitespace-trimmed match at backend.
 */
export async function resolveOrCreateLocationByName(name) {
  const clean = (name || "").replace(/\s+/g, " ").trim();
  if (!clean) return "";
  const { data } = await api.post("/assets/locations/resolve-or-create", { name: clean });
  return data.id;
}

/** Grouped <select> with <optgroup>. Fully controlled, minimal invasive. */
export function LocationSelect({ value, onChange, testid = "location-select",
                                  includeInactive = false, className = "", required = false }) {
  const { groups, loading } = useLocationOptions();
  return (
    <select
      data-testid={testid}
      required={required}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className={className || "w-full px-2 py-1.5 border border-slate-300 text-sm bg-white"}
    >
      <option value="">— Pilih Lokasi —</option>
      {loading && <option value="" disabled>Memuat…</option>}
      {groups.map((g) => {
        if (g.key === "legacy_inactive" && !includeInactive) return null;
        if (!g.items?.length) return null;
        return (
          <optgroup key={g.key} label={g.label}>
            {g.items.map((it) => {
              const suffix = it.city ? ` · ${it.city}` : "";
              const badge = it.project_status ? ` [${it.project_status.toUpperCase()}]` : "";
              const src = it.source === "project_virtual" || it.source === "project_linked" ? " (Project)" : "";
              return (
                <option key={it.id} value={it.id}>
                  {it.name}{suffix}{badge}{src}
                </option>
              );
            })}
          </optgroup>
        );
      })}
    </select>
  );
}

/**
 * 16-ASSET-PIC-LOCATION-R1: LocationCombo — input yang bisa diketik ATAU dipilih
 * dari datalist ter-group (Kantor/Internal, Project Aktif, Lokasi Lain).
 *
 * Perbedaan dgn LocationSelect: menyimpan NAMA (string) di state, bukan id.
 * Konversi ke id dilakukan di parent form via `resolveOrCreateLocationByName`.
 *
 * Props:
 *   value: nama lokasi (string)
 *   onChange(name): callback saat user ubah
 *   knownNamesMap: opsional Map(nama_lower → id) untuk auto-resolve saat pilih
 *                  dari datalist (skip create).
 */
export function LocationCombo({ value, onChange, testid = "location-combo", className = "", groups, loading }) {
  const listId = `${testid}-list`;
  const groupLabel = {
    kantor_gudang: "KANTOR / INTERNAL",
    project_active: "PROJECT AKTIF",
    project_closing: "PROJECT CLOSING",
    vendor_servis: "VENDOR / SERVIS",
    lainnya: "LOKASI LAIN",
  };
  // Dedup names (case-insensitive) — datalist tidak dukung optgroup, kita
  // pakai label meta di option untuk memisahkan visual.
  const flat = [];
  const seen = new Set();
  (groups || []).forEach((g) => {
    if (g.key === "legacy_inactive") return;
    (g.items || []).forEach((it) => {
      const key = (it.name || "").trim().toLowerCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      flat.push({ name: it.name, city: it.city, group: groupLabel[g.key] || g.label });
    });
  });
  return (
    <>
      <input
        type="text"
        list={listId}
        data-testid={testid}
        value={value || ""}
        placeholder={loading ? "Memuat…" : "Pilih atau ketik nama lokasi baru"}
        onChange={(e) => onChange(e.target.value)}
        className={className || "w-full px-2 py-1.5 border border-slate-300 text-sm bg-white"}
        autoComplete="off"
      />
      <datalist id={listId}>
        {flat.map((it) => (
          <option key={`${it.group}-${it.name}`} value={it.name}>
            {it.group}{it.city ? ` · ${it.city}` : ""}
          </option>
        ))}
      </datalist>
    </>
  );
}
